import { GameConfig } from '@/types/game';

export interface InputHandlerOptions {
  canvas: HTMLCanvasElement;
  config: GameConfig;
  onCellClick: (x: number, y: number) => void;
  onPieceMove?: (direction: 'left' | 'right') => void;
}

export class InputHandler {
  private canvas: HTMLCanvasElement;
  private config: GameConfig;
  private onCellClick: (x: number, y: number) => void;
  private onPieceMove?: (direction: 'left' | 'right') => void;
  private touchStartX: number = 0;

  constructor(options: InputHandlerOptions) {
    this.canvas = options.canvas;
    this.config = options.config;
    this.onCellClick = options.onCellClick;
    this.onPieceMove = options.onPieceMove;
    
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Mouse click for desktop
    this.canvas.addEventListener('click', this.handleClick.bind(this));
    
    // Touch events - simplified!
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        this.touchStartX = e.touches[0].clientX;
      }
    }, { passive: false });
    
    this.canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      if (e.changedTouches.length > 0) {
        const touchEndX = e.changedTouches[0].clientX;
        const deltaX = touchEndX - this.touchStartX;
        
        // If swipe is detected (more than 30 pixels), move the piece
        if (Math.abs(deltaX) > 30) {
          if (this.onPieceMove) {
            this.onPieceMove(deltaX > 0 ? 'right' : 'left');
          }
        } else {
          // Otherwise it's a tap - handle as click
          const touch = e.changedTouches[0];
          const rect = this.canvas.getBoundingClientRect();
          const x = Math.floor((touch.clientX - rect.left) / this.config.cellSize);
          const y = Math.floor((touch.clientY - rect.top) / this.config.cellSize);
          
          if (x >= 0 && x < this.config.cols && y >= 0 && y < this.config.rows) {
            this.onCellClick(x, y);
          }
        }
      }
    }, { passive: false });
    
    // Prevent scrolling on touch move
    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
    }, { passive: false });
    
    // Keyboard support
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  private handleClick(event: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = Math.floor((event.clientX - rect.left) / this.config.cellSize);
    const y = Math.floor((event.clientY - rect.top) / this.config.cellSize);
    
    if (x >= 0 && x < this.config.cols && y >= 0 && y < this.config.rows) {
      this.onCellClick(x, y);
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        this.onCellClick(-1, -1); // Deselect
        break;
      case 'ArrowLeft':
        if (this.onPieceMove) {
          event.preventDefault();
          this.onPieceMove('left');
        }
        break;
      case 'ArrowRight':
        if (this.onPieceMove) {
          event.preventDefault();
          this.onPieceMove('right');
        }
        break;
    }
  }

  destroy(): void {
    // Clean up event listeners
  }
}